import * as Sentry from '@sentry/vue';
import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/vue';
// `TransactionEvent` is not re-exported from `@sentry/vue`'s public type surface
// (only `ErrorEvent`/`Event` are), so the transaction-hook type comes from
// `@sentry/core` — the package that actually declares `beforeSendTransaction`'s
// signature. Type-only import: erased at build, no runtime dependency added.
import type { TransactionEvent } from '@sentry/core';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import { resolveRuntimeEnv, isUnauthorizedError } from '@sera/core';

// quick-260629-lm6 — re-export the Sentry user/tenant context helpers so app-side
// callers keep importing Sentry helpers from this one app plugin (centralization
// intent). The implementation physically lives in @sera/core because the
// leaf-level auth store must reach it and libs/core cannot import this app module
// (dependency direction: apps depend on @sera/core, never the reverse).
export { setSentryUser, clearSentryUser } from '@sera/core';
export type { SentryUser } from '@sera/core';

/**
 * Plan 05.5-09 Task 2 — financial-PII denylist + scrubber.
 *
 * Closes T-05.5-12 / T-05.5-21 / T-05.5-22: financial values (cents amounts,
 * interest rates, APRs, down-payment / loan-amount / payment-amount keys) must
 * not cross the trust boundary into Sentry breadcrumbs, extras, contexts, tags,
 * request.query_string, or session replay.
 *
 * Regex (case-insensitive):
 *   /(_cents$|^interest_rate$|^apr$|^down_payment_|^loan_amount_|^payment_amount_)/i
 *
 * Matches:
 *   - any key ending with `_cents`            (e.g. balance_cents, payment_amount_cents)
 *   - exact `interest_rate` / `apr`           (case-insensitive)
 *   - any key beginning with `down_payment_`  (e.g. down_payment_cents, down_payment_dollars)
 *   - any key beginning with `loan_amount_`   (e.g. loan_amount_cents)
 *   - any key beginning with `payment_amount_`(e.g. payment_amount_cents)
 *
 * Deliberate context shape from Plan 05.5-05 `captureException({ tenant,
 * quoteId, downPaymentCents })`: `downPaymentCents` is intentionally scrubbed.
 * The store-side cap-reached handler was updated in Plan 05.5-05 to drop the
 * deliberate key (see `useQuotesStore.handleFinancingCapReached`); triage uses
 * `{ tenant, quoteId }` only — by design.
 *
 * Behavioral guarantees:
 *   - Returns a NEW object (does not mutate the input).
 *   - Recursive on plain objects, depth-bounded at 4 to prevent runaway.
 *   - Handles null / undefined / non-object input defensively (returns `{}`).
 *   - Arrays are walked but their elements are not deep-scrubbed unless they
 *     are themselves plain objects (matches Sentry breadcrumb data shape).
 */
/**
 * Phase 5.3-05 Task 3 — denylist EXTENDED for payment cardholder/bank data
 * (T-05.3-15). The 05.5-09 financial-value keys stay; we add the PCI / ACH
 * surface so a PAN / CVV / routing / account number / token / secret can never
 * cross into Sentry breadcrumbs, extras, contexts, tags, or request query
 * strings. `card.?number` / `account.?number` match both `card_number` and
 * `cardnumber` spellings.
 */
// NOTE on `(?:^|_)pan(?:_|$)`: the PAN alternative MUST stay delimited by
// string-start/end or underscores. An unanchored bare `pan` matched the
// substring inside `span_id` (and `parent_span_id`) — every traced event
// carries `contexts.trace.span_id`, so the scrubber rewrote it to `[scrubbed]`
// and Sentry discarded it as "not a valid span id" (Event Processing Error).
// The real PAN key forms (`pan`, `card_pan`, `pan_number`) are still matched;
// `card_number`/`account_number`/value-shape PAN scrubbing cover the rest.
const FINANCIAL_PII_KEY_REGEX =
  /(_cents$|^interest_rate$|^apr$|^down_payment_|^loan_amount_|^payment_amount_|(?:^|_)pan(?:_|$)|card.?number|cvv|cvc|cvn|routing|account.?number|token|authorization|secret|api.?key|ssn)/i;
const SCRUBBED = '[scrubbed]';
const MAX_DEPTH = 4;

/**
 * Phase 5.3-05 — VALUE-SHAPE scrubbing. Some leaks are keyed innocuously (e.g.
 * `value`, `text`, an array element) but carry a card or routing number in the
 * VALUE. We redact any string whose shape is a bare PAN (13–19 digits, allowing
 * spaces/dashes between groups). This is a defense-in-depth pass over the key
 * denylist, not a replacement.
 *
 * WR-02: the 9-digit routing scrub is NOT applied unconditionally. A bare
 * 9-digit string is an extremely common shape for innocuous ids (invoice ids,
 * reference ids, ZIP+4 concatenations, some transaction reference ids), and
 * destroying those in triage degrades the observability the rest of this file
 * works to provide. The PAN scrub (13–19 digits) errs safe and stays
 * unconditional; the routing scrub fires ONLY when the surrounding KEY hints at
 * bank context (e.g. `routing`, `aba`, `bank_*`). When there is no key context
 * (array elements), the routing scrub does not fire — a 9-digit routing number
 * keyed innocuously is still caught by the `routing`/`account` KEY denylist on
 * its real field. PAN/CVV redaction is unchanged.
 */
const PAN_VALUE_REGEX = /\b(?:\d[ -]?){13,19}\b/;
const ROUTING_VALUE_REGEX = /^\d{9}$/;
// Keys whose context makes a bare 9-digit value a likely bank routing number.
const BANK_CONTEXT_KEY_REGEX = /(routing|\baba\b|bank)/i;

function scrubValueShape(value: unknown, key?: string): unknown {
  if (typeof value !== 'string') return value;
  const digits = value.replace(/[ -]/g, '');
  if (
    digits.length >= 13 &&
    digits.length <= 19 &&
    PAN_VALUE_REGEX.test(value)
  ) {
    return SCRUBBED;
  }
  // Routing scrub is scoped to bank-context keys (WR-02) to avoid over-redacting
  // innocuous 9-digit ids.
  if (
    digits.length === 9 &&
    key != null &&
    BANK_CONTEXT_KEY_REGEX.test(key) &&
    ROUTING_VALUE_REGEX.test(digits)
  ) {
    return SCRUBBED;
  }
  return value;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

export function scrubFinancialPII(
  obj: Record<string, unknown>,
  depth = 0,
): Record<string, unknown> {
  if (!isPlainObject(obj)) return {};
  if (depth >= MAX_DEPTH) {
    // Past the depth bound — return a shallow copy unchanged. This prevents
    // pathological inputs (deeply nested or cyclic-ish) from blowing the stack
    // while still surfacing the upper layers correctly.
    return { ...obj };
  }
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (FINANCIAL_PII_KEY_REGEX.test(key)) {
      out[key] = SCRUBBED;
      continue;
    }
    if (isPlainObject(value)) {
      out[key] = scrubFinancialPII(value, depth + 1);
    } else if (Array.isArray(value)) {
      // Array items carry the parent key as their bank-context hint (WR-02): a
      // `bank_routing: [...]` array scrubs, an `ids: [...]` array does not.
      out[key] = value.map((item) =>
        isPlainObject(item)
          ? scrubFinancialPII(item, depth + 1)
          : scrubValueShape(item, key),
      );
    } else {
      // Value-shape pass (Phase 5.3-05): redact a bare PAN unconditionally, and
      // a bare routing number only under a bank-context key (WR-02).
      out[key] = scrubValueShape(value, key);
    }
  }
  return out;
}

/**
 * Plan 05.5-09 — `query_string` scrubber for `event.request.query_string`.
 *
 * Walks each `key=value` pair; if the key matches the financial-PII regex,
 * replace its value with `[scrubbed]` (URL-encoded as `%5Bscrubbed%5D`).
 * Preserves non-matching params verbatim.
 *
 * Handles both string query_string (Sentry's typical shape) and the `?`-prefix
 * variant. Returns the original string unchanged when it cannot be parsed as
 * key=value pairs.
 */
function scrubQueryString(raw: string): string {
  if (typeof raw !== 'string' || raw.length === 0) return raw;
  const stripped = raw.startsWith('?') ? raw.slice(1) : raw;
  const params = stripped.split('&');
  const out = params
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq < 0) return pair;
      const key = pair.slice(0, eq);
      // Decode for matching, but re-emit the raw key (so encoded keys stay
      // encoded — matches what Sentry receives from the browser).
      const decodedKey = (() => {
        try {
          return decodeURIComponent(key);
        } catch {
          return key;
        }
      })();
      if (FINANCIAL_PII_KEY_REGEX.test(decodedKey)) {
        return `${key}=${encodeURIComponent(SCRUBBED)}`;
      }
      return pair;
    })
    .join('&');
  return raw.startsWith('?') ? `?${out}` : out;
}

/**
 * Plan 08-04 (PAYLINK-16 / D-09 / D-10) — pay-URL redaction.
 *
 * Closes two telemetry-leak vectors the key/query scrubbers miss:
 *   1. the `/pay/<link-token>` PATH segment (covered by nothing today —
 *      `scrubFinancialPII` is key-based, `scrubQueryString` only touches
 *      `request.query_string`, neither reaches a path segment); and
 *   2. the session `temp_token` VALUE inside full-URL strings (`request.url`,
 *      breadcrumb `data.url`/`from`/`to`, `message`) that are never split into
 *      `query_string`, so the `token`-keyed denylist never sees them.
 *
 * Pure function: string input → redacted string; non-string input returned
 * unchanged (guard). The path replace is global (any `/pay/<seg>`); the
 * `temp_token` replace is global + case-insensitive over `?`/`&` params. No
 * `?redirect=` is ever built on this route, so this is deep defense-in-depth for
 * the URL path + query (D-10).
 */
export function redactPayUrl(url: string): string {
  if (typeof url !== 'string') return url;
  return url
    .replace(/(\/pay\/)[^/?#]+/g, '$1[redacted]') // link-token path segment
    .replace(/([?&]temp_token=)[^&#]*/gi, '$1[redacted]'); // temp_token value
}

/**
 * Plan 08-04 — apply `redactPayUrl` to the URL-carrying fields of a breadcrumb's
 * `data` (http/navigation breadcrumbs put the full URL in `url`/`from`/`to`).
 * Returns a new object; only string fields are touched.
 */
function redactPayUrlFields(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...data };
  for (const field of ['url', 'from', 'to'] as const) {
    if (typeof out[field] === 'string') {
      out[field] = redactPayUrl(out[field] as string);
    }
  }
  return out;
}

/**
 * Plan 08-04 — full breadcrumb pay-URL redaction: the top-level `message` (a
 * navigation breadcrumb carries the URL there) plus the `data` URL fields. The
 * existing financial-key `scrubFinancialPII` pass runs first so both guarantees
 * hold; shared by `composedBeforeSend` and `composedBeforeBreadcrumb`.
 */
function redactBreadcrumbPayUrl(b: Breadcrumb): Breadcrumb {
  const next: Breadcrumb = { ...b };
  if (typeof next.message === 'string') {
    next.message = redactPayUrl(next.message);
  }
  if (next.data && isPlainObject(next.data)) {
    next.data = redactPayUrlFields(scrubFinancialPII(next.data));
  }
  return next;
}

/**
 * quick-260708-s41 — DROP 401s (CUSTOMER-PORTAL-T). A 401 is an expected
 * condition here, not an actionable bug: session expiry, a resource-permission
 * denial (`isResourcePermissionDenialAs401` in axiosInstance), or the boot-time
 * `findByEmail` probe hitting a stale stored token. Every one of these funneled
 * into Sentry (three view `captureException` sites plus the SDK's global
 * unhandled-rejection handler), spawning recurring noise issues with 0 users
 * impacted. We drop the event and leave an `info` breadcrumb instead, so a 401
 * still appears in the trail of any subsequent REAL error without generating an
 * issue of its own. Detection reuses the shared `isUnauthorizedError` guard.
 *
 * Returns `true` (and records the breadcrumb) when the event's originating
 * exception is a 401; `false` otherwise. Extracted from `composedBeforeSend` to
 * keep that function under the cyclomatic-complexity ceiling.
 */
export function shouldSuppressUnauthorized(hint?: EventHint): boolean {
  if (!isUnauthorizedError(hint?.originalException)) return false;
  Sentry.addBreadcrumb({
    category: 'http',
    type: 'http',
    level: 'info',
    message: 'Suppressed 401 (unauthorized) — not reported to Sentry',
    data: {
      url: (hint?.originalException as { config?: { url?: string } })?.config
        ?.url,
    },
  });
  return true;
}

/**
 * Plan 05.5-09 — composed `beforeSend` hook.
 *
 * Exported so the unit spec can exercise the full event-scrubbing pipeline
 * without invoking `Sentry.init()`. The runtime wiring registers this as the
 * `beforeSend` option in `Sentry.init()` below.
 *
 * Existing Sentry plugin behavior (Vue tracing, replay, dsn config, env
 * exposure) is preserved — this scrubber is ADDITIVE: it does not remove any
 * fields from the event, it only mutates values whose key matches the denylist.
 *
 * quick-260708-s41 — 401s are dropped BEFORE this scrub pass via
 * `shouldSuppressUnauthorized` (see below). Everything reaching the scrub logic
 * is a non-401 event.
 */
export function composedBeforeSend(
  event: ErrorEvent,
  hint?: EventHint,
): ErrorEvent | null {
  if (shouldSuppressUnauthorized(hint)) return null;

  const scrubbed: ErrorEvent = { ...event };

  if (scrubbed.extra) {
    scrubbed.extra = scrubFinancialPII(
      scrubbed.extra as Record<string, unknown>,
    );
  }
  if (scrubbed.contexts) {
    scrubbed.contexts = scrubFinancialPII(
      scrubbed.contexts as Record<string, unknown>,
    ) as ErrorEvent['contexts'];
  }
  // WR-03: stamp the live payment tags (if a payment is in flight) BEFORE the
  // scrub pass, so they are emitted only for the lifetime of the active payment
  // context and never leak onto a post-close unrelated error.
  const taggedWithPayment = withPaymentTags(
    scrubbed.tags as Record<string, unknown> | undefined,
  );
  if (taggedWithPayment) {
    scrubbed.tags = scrubFinancialPII(taggedWithPayment) as ErrorEvent['tags'];
  }
  if (Array.isArray(scrubbed.breadcrumbs)) {
    // Plan 08-04 — redactBreadcrumbPayUrl runs the existing financial-key scrub
    // AND the pay-URL pass over message + data url/from/to.
    scrubbed.breadcrumbs = scrubbed.breadcrumbs.map(redactBreadcrumbPayUrl);
  }
  if (scrubbed.request) {
    const req = { ...scrubbed.request };
    if (typeof req.query_string === 'string') {
      req.query_string = scrubQueryString(req.query_string);
    }
    // Plan 08-04 — the full `request.url` carries path+query as one string that
    // query_string scrubbing never sees; redact the pay path + temp_token here.
    if (typeof req.url === 'string') {
      req.url = redactPayUrl(req.url);
    }
    scrubbed.request = req;
  }
  return scrubbed;
}

/**
 * Plan 05.5-09 — composed `beforeBreadcrumb` hook.
 *
 * Scrubs `breadcrumb.data` financial keys in flight (independent of the
 * eventual `beforeSend` pass) so breadcrumbs ALSO get filtered before they
 * land in the event's `breadcrumbs[]` array. Belt-and-suspenders against
 * session-replay capture of click events that contain dollar values.
 *
 * Plan 08-04 — ALSO redacts the pay-URL (path segment + temp_token) from the
 * breadcrumb `message` and `data.url`/`from`/`to`, so a navigation/http
 * breadcrumb carrying `/pay/<token>?temp_token=…` never reaches the event trail.
 */
export function composedBeforeBreadcrumb(
  breadcrumb: Breadcrumb,
): Breadcrumb | null {
  return redactBreadcrumbPayUrl(breadcrumb);
}

/**
 * Plan 08-04 (PAYLINK-16 / D-09) — composed `beforeSendTransaction` hook.
 *
 * There was NO `beforeSendTransaction` before this plan. `browserTracingIntegration`
 * names transactions from the parameterized route (`/pay/:token`, token not
 * usually in the name), but the transaction event's `request.url` can carry the
 * resolved URL with the link token + `temp_token`. This runs `redactPayUrl` over
 * `request.url` and the `transaction` name — defense-in-depth closing that
 * vector. Exported so the unit spec exercises it without `Sentry.init()`.
 */
export function composedBeforeSendTransaction(
  event: TransactionEvent,
): TransactionEvent {
  const scrubbed: TransactionEvent = { ...event };
  if (scrubbed.request && typeof scrubbed.request.url === 'string') {
    scrubbed.request = {
      ...scrubbed.request,
      url: redactPayUrl(scrubbed.request.url),
    };
  }
  if (typeof scrubbed.transaction === 'string') {
    scrubbed.transaction = redactPayUrl(scrubbed.transaction);
  }
  return scrubbed;
}

/**
 * Phase 5.3-05 Task 3 — payment observability context (research Observability,
 * AC#14). Call `beginPaymentContext` when the pay modal opens and
 * `endPaymentContext` when it closes. These set/clear Sentry TAGS (queryable in
 * triage) plus a `payment` CONTEXT. `amount_cents` is allowed here — it is an
 * invoice total, NOT cardholder data, and is useful for correlating a failed
 * charge to its invoice. The denylist still scrubs everything PCI/ACH.
 */
export interface PaymentContext {
  tenant: string;
  gateway: 'stax' | 'globalpayments';
  method: 'card' | 'ach' | 'payment_method';
  invoiceId: number | string;
  amountCents?: number;
}

// WR-03: the queryable `payment.*` tags are derived from this module-level
// active context inside `composedBeforeSend` rather than written as long-lived
// global tags. `Sentry.setTag(key, undefined)` is not a documented way to clear
// a tag and is version-dependent — depending on the SDK it can serialize the
// literal `undefined` or leave the prior value resident on the scope, so a
// post-close unrelated error could carry a stale `payment.gateway` /
// `payment.invoice_id` tag and mis-attribute a non-payment error to a payment
// incident. By scoping the tags to the lifetime of `activePaymentContext`
// (set on begin, nulled on end), the tags exist on an event IFF a payment is
// in flight — clearing is reliable regardless of SDK version.
let activePaymentContext: PaymentContext | null = null;

export function beginPaymentContext(ctx: PaymentContext): void {
  activePaymentContext = ctx;
  Sentry.setContext('payment', {
    tenant: ctx.tenant,
    gateway: ctx.gateway,
    method: ctx.method,
    invoice_id: ctx.invoiceId,
    // amount_cents is OK (not cardholder data); useful for correlation.
    amount_cents: ctx.amountCents,
  });
}

export function endPaymentContext(): void {
  activePaymentContext = null;
  // `setContext('payment', null)` reliably clears the context; the tags follow
  // the active-context lifetime via composedBeforeSend (WR-03).
  Sentry.setContext('payment', null);
}

/**
 * D-04 / BILL-11 ACH authorization audit trail (repudiation threat T-05.3-18).
 * The customer's explicit ACH consent must leave a durable trace beyond the
 * Pendo funnel event — a Sentry breadcrumb carrying the terms version so an
 * error event captured later in the same payment intent shows consent was
 * confirmed (and against which version of the cleared authorization copy).
 */
export function addAchConsentBreadcrumb(termsVersion: string): void {
  Sentry.addBreadcrumb({
    category: 'payment.ach_consent',
    message: 'ACH authorization consent confirmed',
    data: { terms_version: termsVersion },
    level: 'info',
  });
}

/**
 * Stamp the `payment.*` triage tags onto an outgoing event from the live
 * `activePaymentContext` (WR-03). Returns the event's tags with the payment
 * tags applied only while a payment is in flight, so they never leak onto an
 * unrelated post-close error.
 */
function withPaymentTags(
  tags: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  const ctx = activePaymentContext;
  if (!ctx) return tags;
  return {
    ...(tags ?? {}),
    'payment.tenant': ctx.tenant,
    'payment.gateway': ctx.gateway,
    'payment.method': ctx.method,
    'payment.invoice_id': String(ctx.invoiceId),
  };
}

/**
 * Phase 5.3-05 Task 3 — SDK-load-failure helper (research Observability, AC#14).
 *
 * A `<script>.onerror` is invisible to try/catch, so a gateway CDN that won't
 * load would otherwise vanish silently. This is an INCIDENT (the customer can't
 * pay), not a user error — capture it as `fatal` with a stable fingerprint so
 * all load failures for a gateway group into one issue. Distinct from a bank
 * decline, which stays a quiet breadcrumb (Pitfall 6).
 */
export function captureGatewaySdkLoadFailure(
  gateway: 'stax' | 'globalpayments',
  cause?: unknown,
): void {
  Sentry.captureException(
    cause instanceof Error
      ? cause
      : new Error(`[payment] gateway SDK failed to load: ${gateway}`),
    {
      level: 'fatal',
      fingerprint: ['payment-sdk-load-failure', gateway],
      tags: { 'payment.gateway': gateway },
    },
  );
}

/**
 * Phase 5.3-05 Task 3 — Stripe config-anomaly guard (T-05.3-16).
 *
 * Stripe is DEAD CODE (CLAUDE.md): no tenant uses it. If the resolved
 * `gatewayType` is `'stripe'` (the TenantService fallback when neither
 * Stax nor GPI is enabled), that is a DATA ANOMALY to alert on — never a silent
 * failure, never a Stripe SDK load. Fires a fingerprinted `captureMessage` so
 * every occurrence groups into one alertable issue. This is the ONLY mention of
 * Stripe in the payment path — there is no Stripe import anywhere.
 */
export function guardStripeGatewayAnomaly(gatewayType: string): boolean {
  if (gatewayType !== 'stripe') return false;
  Sentry.captureMessage('config-anomaly-stripe-gateway', {
    level: 'error',
    fingerprint: ['config-anomaly-stripe-gateway'],
    tags: { 'payment.gateway': 'stripe' },
  });
  return true;
}

/**
 * TOF-601 — payment-CSP-block telemetry.
 *
 * staxjs does not tokenize card fields itself: it looks up the merchant's underlying
 * PROCESSOR and frames the card-number/CVV iframes from THAT processor's domain
 * (BlockChyp, NMI, Spreedly, PayGateway). Each such host must be in the CSP `frame-src`
 * (see apps/customer-portal/index.html). When a processor domain is missing, the card
 * iframe is CSP-BLOCKED and the form never renders — but a CSP block throws ONLY a
 * console error: it never reaches the gateway SDK's `onerror`, so it was invisible in
 * Sentry AND the submit-gated Pendo `payment_failed` (the original TOF-601 blind spot;
 * it showed only as a `payment_modal_opened` → `payment_attempted` funnel drop).
 *
 * This reports a blocked PAYMENT host as a fingerprinted incident so a future
 * missing-processor-domain gap alerts immediately. Non-payment violations (e.g. Google
 * reCAPTCHA's own report-only `frame-ancestors`) are ignored to keep the signal clean.
 */
const PAYMENT_CSP_HOSTS = [
  'blockchyp.com',
  'networkmerchants.com',
  'spreedly.com',
  'paygateway.com',
  'staxpayments.com',
  'fattlabs.com',
  'fattmerchant.com',
];

export function captureCspPaymentViolation(
  e: Pick<
    SecurityPolicyViolationEvent,
    'blockedURI' | 'violatedDirective' | 'disposition'
  >,
): boolean {
  let host = '';
  try {
    host = new URL(e.blockedURI).hostname;
  } catch {
    // 'inline' / 'eval' / '' and other keyword blockedURIs are not processor hosts.
    return false;
  }
  const isPaymentHost = PAYMENT_CSP_HOSTS.some(
    (h) => host === h || host.endsWith(`.${h}`),
  );
  if (!isPaymentHost) return false;
  Sentry.captureMessage('payment-csp-block', {
    level: 'error',
    fingerprint: ['payment-csp-block', e.violatedDirective, host],
    tags: {
      'payment.csp.directive': e.violatedDirective,
      'payment.csp.blocked_host': host,
      'payment.csp.disposition': e.disposition,
    },
  });
  return true;
}

export function initSentry(app: App, router: Router): void {
  const isDev = import.meta.env.DEV;
  // Environment is derived from the runtime hostname (same source as the API URL
  // / cable host), NOT a build-time VITE_ENV: the app builds once with no per-env
  // target, so VITE_ENV is empty and would otherwise tag every deploy (staging
  // AND prod) as "local". See resolveRuntimeEnv in @sera/core.
  const env = resolveRuntimeEnv();
  // Never capture from a developer machine. `!isDev` covers the Vite dev server;
  // the localhost check additionally covers the production build served via
  // `vite preview` (E2E + local prod preview), where import.meta.env.DEV is
  // false — without this, localhost would be classified as production and emit
  // real events (and run replay buffering) from dev/CI.
  const host = window.location.hostname;
  const isLocalhost =
    host === 'localhost' || host === '127.0.0.1' || host === '[::1]';

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: env,
    enabled: !!import.meta.env.VITE_SENTRY_DSN && !isDev && !isLocalhost,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      // Phase 5.3-05 Task 3 — replay lockdown for the payment surface
      // (T-05.3-15). Block the gateway iframes and any `.payment-fields`
      // container (the marker class the gateway forms carry) plus an explicit
      // `[data-sentry-block]` escape hatch, so hosted-field input is NEVER
      // recorded. `maskAllInputs` masks every other input value; an empty
      // `networkDetailAllowUrls` keeps request/response bodies out of replays.
      Sentry.replayIntegration({
        block: ['iframe', '.payment-fields', '[data-sentry-block]'],
        maskAllInputs: true,
        networkDetailAllowUrls: [],
      }),
      Sentry.vueIntegration({
        tracingOptions: {
          trackComponents: true,
          hooks: ['activate', 'mount', 'update'],
        },
      }),
    ],
    tracesSampleRate: 0.05,
    // Session Replay is fully DISABLED — both rates are 0, so no session or
    // on-error replay is ever recorded or sent (the replayIntegration above is
    // configured for payment-surface lockdown but never samples). This was
    // previously `replaysOnErrorSampleRate: isProd ? 0.05 : 0` where
    // `isProd === (VITE_ENV === 'prod')` was ALWAYS false (VITE_ENV is empty in
    // every single-build deploy), so 0 was the effective value everywhere.
    // Enabling replay is deliberately NOT done here: it perturbs the E2E suite
    // (replay buffering changes logout timing) and needs its own validation.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // Plan 05.5-09 — financial-PII denylist (T-05.5-12 / T-05.5-21 / T-05.5-22).
    // Composed hooks are exported above so the unit spec can exercise them
    // without booting Sentry. This wiring is additive: existing replay /
    // tracing / vueIntegration behavior is unchanged.
    beforeSend: composedBeforeSend,
    beforeBreadcrumb: composedBeforeBreadcrumb,
    // Plan 08-04 (PAYLINK-16 / D-09) — redact the `/pay/<link-token>` path
    // segment and the `temp_token` value from transaction events too (request.url
    // + transaction name). Additive; no other tracing behavior changes.
    beforeSendTransaction: composedBeforeSendTransaction,
    // PII controls. We rely primarily on the composed beforeSend /
    // beforeBreadcrumb scrubbers above plus the replay denylist for
    // payment-surface lockdown. `dataCollection` is a coarser SDK-level
    // toggle left disabled here for documentation/future use; uncomment to
    // stop sending user data and HTTP bodies entirely. See:
    // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#dataCollection
    // dataCollection: {
    //   userInfo: false,
    //   httpBodies: [],
    // },
  });

  // TOF-601 — capture CSP-blocked payment-processor frames/scripts (see
  // captureCspPaymentViolation). Registered once at init; off-prod it is a no-op
  // because Sentry.init above is `enabled: false` there.
  window.addEventListener('securitypolicyviolation', (e) => {
    captureCspPaymentViolation(e);
  });
}
